<template>
  <div class="video-player-wrapper" :class="{ 'hide-plyr-play': pendingTapToPlay }">
    <transition name="overlay-fade">
      <div v-if="showLoadingOverlay" class="video-loading-overlay" :style="{ background: $vuetify.theme.dark ? '#121212' : '#fff' }">
        <v-progress-circular
          indeterminate
          color="primary"
          :size="56"
          :width="4"
        />
      </div>
    </transition>
    <video-tap-to-play-overlay />
    <div class="video-player-container" :class="{ 'dark-theme': $vuetify.theme.dark }">
      <video ref="videoContainer" playsinline controls></video>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VideoService from '@/services/VideoService';
import VideoTapToPlayOverlay from '@/components/player/VideoTapToPlayOverlay.vue';

export default {
  name: 'VideoPlayer',

  components: {
    VideoTapToPlayOverlay,
  },

  data() {
    return {
      showLoadingOverlay: true,
      loadingOverlayTimeout: null,
    };
  },

  computed: {
    ...mapGetters({
      currentVideo: 'video/currentVideo',
      currentVideoDetails: 'video/currentVideoDetails',
      pendingTapToPlay: 'video/pendingTapToPlay',
    }),

    currentVideoId() {
      return this.currentVideoDetails?.youtubeId || this.currentVideo?.youtubeId || null;
    },
  },

  watch: {
    currentVideoId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.loadCurrentVideo();
      }
    },
  },

  mounted() {
    this.initializePlayer();

    // Fade out loading overlay after 1.5 seconds
    this.loadingOverlayTimeout = setTimeout(() => {
      this.showLoadingOverlay = false;
    }, 1500);

    // Set up callbacks
    VideoService.setVideoEndedCallback(() => {
      this.handleVideoEnded();
    });

    VideoService.setProgressUpdateCallback((progressData) => {
      this.updateProgress(progressData);
    });

    VideoService.setAutoplayBlockedCallback(() => {
      this.$store.commit('video/SET_IS_PLAYING', false);
    });
  },

  beforeDestroy() {
    clearTimeout(this.loadingOverlayTimeout);
    VideoService.setAutoplayBlockedCallback(null);
    VideoService.destroy();
  },

  methods: {
    ...mapActions({
      initializeVideoPlayer: 'video/initializePlayer',
      handleVideoEnded: 'video/handleVideoEnded',
      updateProgress: 'video/updateProgress',
    }),

    initializePlayer() {
      // Get theme colors for customization
      const isDark = this.$vuetify.theme.dark;

      const plyrOptions = {
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'duration',
          'mute',
          'volume',
          'settings',
          'pip',
          'fullscreen',
        ],
        youtube: {
          noCookie: false,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          playsinline: 1,
        },
        ratio: '16:9',
        autoplay: false,
        // Custom styling to match Vuetify theme
        ...(isDark && {
          colors: {
            main: '#1976D2',
          },
        }),
      };

      VideoService.initialize(this.$refs.videoContainer, plyrOptions);

      // Initialize the first video
      this.initializeVideoPlayer();
    },

    loadCurrentVideo() {
      // Video loading is handled by VideoService.loadVideo() called from Vuex store actions
    },
  },
};
</script>

<style scoped>
/*
  Video player wrapper - full width up to container max (800px)
  Maintains consistent margin-top to match logo positioning
*/

.video-player-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 auto;
  /* Default (> 1270px): matches logo margin-top */
  margin-top: 105px;
  position: relative;
}

/* Loading overlay - covers the video area with a centered spinner */
.video-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

/* Fade-out transition for the overlay */
.overlay-fade-leave-active {
  transition: opacity 0.5s ease;
}
.overlay-fade-leave-to {
  opacity: 0;
}

.video-player-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  /* 16:9 aspect ratio - height is calculated from width */
  aspect-ratio: 16 / 9;
}

/* Breakpoint: ≤ 1270px (lg lower, md) */
@media only screen and (max-width: 1270px) {
  .video-player-wrapper {
    margin-top: 40px;
  }
}

/* Breakpoint: ≤ 960px (md, sm) - matches logo margin-top change */
@media only screen and (max-width: 960px) {
  .video-player-wrapper {
    margin-top: 30px;
  }
}

/* Breakpoint: ≤ 599px (xs) - mobile with fixed playerContainer and compact header */
@media only screen and (max-width: 599px) {
  .video-player-wrapper {
    margin-top: 0px;  /* No extra margin - sits right below compact header (~40px) */
  }
}

/* Plyr dark theme styling */
.dark-theme >>> .plyr {
  --plyr-color-main: #1976D2;
}

/* Prevent select on video player */
.video-player-container {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Ensure Plyr iframe fills the container */
.video-player-container >>> .plyr {
  height: 100%;
}

.video-player-container >>> .plyr__video-embed {
  height: 100%;
}

/* Hide Plyr's large play button when our tap-to-play overlay is showing */
.hide-plyr-play >>> .plyr__control--overlaid {
  display: none;
}
</style>
